import { useMemo, useState } from 'react';
import { Box, ChevronDown, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function akoType(ako) {
  return ako.artifact_type || ako.type || 'Artifact';
}

function akoHooks(ako) {
  return ako.narration_model?.narration_hooks || ako.narration_hooks || [];
}

function akoHighlights(ako) {
  return ako.interaction_model?.highlight_targets || ako.highlight_targets || [];
}

function akoAnimations(ako) {
  return ako.interaction_model?.animation_candidates || ako.animation_opportunities || [];
}

function QualityBadges({ qc }) {
  if (!qc) return null;
  const entries = Object.entries(qc).filter(([, v]) => v === true);
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {entries.map(([k]) => (
        <span
          key={k}
          className="text-[9px] px-1 py-0.5 rounded border border-emerald-500/25 text-emerald-300/80"
        >
          {k.replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}

function MetadataGrid({ metadata }) {
  if (!metadata || typeof metadata !== 'object') return null;
  const keys = [
    'concept_importance',
    'cognitive_load',
    'visualization_importance',
    'learning_stage',
    'teaching_priority',
  ];
  const shown = keys.filter((k) => metadata[k]);
  if (!shown.length) return null;
  return (
    <div className="grid grid-cols-2 gap-1 text-[10px]">
      {shown.map((k) => (
        <div key={k} className="text-slate-500">
          <span className="text-slate-600">{k.replace(/_/g, ' ')}:</span>{' '}
          <span className="text-slate-300">{String(metadata[k])}</span>
        </div>
      ))}
    </div>
  );
}

function VisualModelPreview({ type, visual }) {
  if (!visual || typeof visual !== 'object') return null;

  if (type === 'Diagram' && visual.components?.length) {
    return (
      <div className="space-y-2 text-xs">
        <p className="text-slate-500">Visual model ({visual.diagram_type || 'Diagram'})</p>
        <ul className="space-y-1">
          {visual.components.map((c) => (
            <li key={c.id} className="text-slate-300">
              <span className="text-violet-300 font-mono text-[10px]">{c.id}</span>
              {' — '}
              {c.label || c.name}
              {c.importance != null ? ` (importance ${c.importance})` : ''}
            </li>
          ))}
        </ul>
        {visual.relationships?.length ? (
          <p className="text-slate-500 mt-2">
            {visual.relationships.length} relationship(s)
          </p>
        ) : null}
      </div>
    );
  }

  if (type === 'Explanation') {
    return (
      <div className="text-xs space-y-1">
        {visual.definition ? (
          <p className="text-slate-300">{visual.definition}</p>
        ) : null}
        {visual.core_idea ? (
          <p className="text-slate-400">{visual.core_idea}</p>
        ) : null}
        {visual.reinforcement_points?.length ? (
          <ul className="list-disc pl-4 text-slate-400">
            {visual.reinforcement_points.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  if (type === 'Real World Analogy' && visual.mapping_table?.length) {
    return (
      <ul className="text-xs text-slate-300 space-y-0.5">
        {visual.mapping_table.map((row, i) => (
          <li key={i}>
            {row.concept || row.source} ↔ {row.analogy || row.target}
          </li>
        ))}
      </ul>
    );
  }

  const preview = visual.scenario || visual.summary || visual.definition;
  if (preview) {
    return <p className="text-xs text-slate-400 line-clamp-4">{String(preview)}</p>;
  }

  return (
    <pre className="text-[10px] text-slate-500 font-mono overflow-auto max-h-32">
      {JSON.stringify(visual, null, 2)}
    </pre>
  );
}

function AkoCard({ ako, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const type = akoType(ako);
  const hooks = akoHooks(ako);
  const highlights = akoHighlights(ako);
  const anims = akoAnimations(ako);
  const visual = ako.visual_model || ako.content;

  return (
    <div className="rounded-lg border border-emerald-500/20 bg-emerald-950/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-emerald-950/20"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-emerald-100">
            <span className="font-mono text-[11px] text-emerald-400/90">{ako.artifact_id}</span>
            {' · '}
            {type}
          </p>
          <p className="text-xs text-slate-400 line-clamp-1">{ako.title}</p>
          <p className="text-[10px] text-slate-600 mt-0.5">
            {ako.slide_id} · {ako.concept}
          </p>
        </div>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-3 border-t border-emerald-900/40 pt-3 text-xs">
          {ako.teaching_purpose ? (
            <div>
              <p className="text-slate-500">Teaching purpose</p>
              <p className="text-slate-300">{ako.teaching_purpose}</p>
            </div>
          ) : null}
          <MetadataGrid metadata={ako.educational_metadata} />
          <VisualModelPreview type={type} visual={visual} />
          {ako.semantic_model?.entities?.length ? (
            <div>
              <p className="text-slate-500 mb-1">Semantic entities</p>
              <div className="flex flex-wrap gap-1">
                {ako.semantic_model.entities.map((e) => (
                  <span key={e.id} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                    {e.label || e.id}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {hooks.length ? (
            <div>
              <p className="text-slate-500 mb-1">Narration hooks</p>
              <ul className="list-disc pl-4 text-slate-400 space-y-0.5">
                {hooks.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {highlights.length ? (
            <div>
              <p className="text-slate-500 mb-1">Highlight targets</p>
              <div className="flex flex-wrap gap-1">
                {highlights.map((h) => (
                  <span key={h} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-violet-200">
                    {h}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {anims.length ? (
            <div>
              <p className="text-slate-500 mb-1">Animation candidates</p>
              <div className="flex flex-wrap gap-1">
                {anims.map((a) => (
                  <span key={a} className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/25 text-amber-200/80">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <QualityBadges qc={ako.quality_check} />
        </div>
      ) : null}
    </div>
  );
}

export function ArtifactGenerationV3Panel({ data }) {
  const akos = data?.artifact_knowledge_objects || [];
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const types = useMemo(() => {
    const set = new Set(akos.map((a) => akoType(a)).filter(Boolean));
    return Array.from(set).sort();
  }, [akos]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return akos.filter((a) => {
      const t = akoType(a);
      if (typeFilter && t !== typeFilter) return false;
      if (!q) return true;
      return (
        a.concept?.toLowerCase().includes(q)
        || a.artifact_id?.toLowerCase().includes(q)
        || a.title?.toLowerCase().includes(q)
        || a.slide_id?.toLowerCase().includes(q)
      );
    });
  }, [akos, filter, typeFilter]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-emerald-500/25 bg-emerald-950/15 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-emerald-200">
          <Box className="w-4 h-4" />
          {data.total_akos} Artifact Knowledge Objects
          {data.step === '5A' ? (
            <span className="text-[10px] text-emerald-400/70">(Step 5A)</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <TwinneeLogo className="w-4 h-4" />
          Semantic only — no images, SVG, or PNG
        </div>
        {data.source ? (
          <span className="text-[10px] text-slate-500 ml-auto">Source: {data.source}</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {types.length > 1 ? (
          <Select value={typeFilter || '__all__'} onValueChange={(v) => setTypeFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-8 w-[180px] text-xs border-slate-700 bg-slate-900 text-slate-300">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All types</SelectItem>
              {types.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        {akos.length > 4 ? (
          <input
            type="search"
            placeholder="Filter AKOs…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-[140px] text-sm rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-slate-200"
          />
        ) : null}
      </div>

      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {filtered.map((ako, i) => (
          <AkoCard key={ako.artifact_id} ako={ako} defaultOpen={i < 2} />
        ))}
      </div>
    </div>
  );
}
